import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ACCENT_COLOR_HEX } from '../lib/theme';
import { 
  ChevronDown, Play, Pause, Trash2, 
  MessageSquare, Users, TrendingUp, Clock, 
  Settings, Inbox, Edit, Loader2, Save, X,
  Plus, Search, Check, AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { 
  campaignService, 
  leadsListService, 
  instagramAccountService,
  campaignLeadsListService,
  campaignAccountService,
  campaignMessageService,
  subscriptionService
} from '../lib/supabaseServices';
import { MessageSequencesFull } from './MessageSequencesFull';

const CampaignRow = ({ 
  campaign, 
  darkMode, 
  onStart, 
  onPause, 
  onDelete,
  onUpdate,
  isStarting = false,
  isReady = false
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  
  // Editable data states
  const [editedCampaign, setEditedCampaign] = useState({
    name: campaign.name,
    description: campaign.description || '',
    daily_message_min: campaign.daily_message_min || 20,
    daily_message_max: campaign.daily_message_max || 50,
    send_time_start: campaign.send_time_start?.slice(0, 5) || '09:00',
    send_time_end: campaign.send_time_end?.slice(0, 5) || '17:00',
    timezone: campaign.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    exclude_weekends: campaign.exclude_weekends || false,
    exclude_holidays: campaign.exclude_holidays || false
  });
  
  const [selectedLeadLists, setSelectedLeadLists] = useState(
    campaign.campaign_leads_lists?.map(cll => cll.leads_lists) || []
  );
  
  const [selectedAccounts, setSelectedAccounts] = useState(
    campaign.campaign_accounts?.map(ca => ({
      ...ca.instagram_accounts,
      is_active: ca.is_active
    })) || []
  );
  
  const [messages, setMessages] = useState(null);
  const [messageSettings, setMessageSettings] = useState({
    useAIOptimization: false
  });
  
  // Available options states
  const [availableLeadLists, setAvailableLeadLists] = useState([]);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [searchTermLeads, setSearchTermLeads] = useState('');
  const [searchTermAccounts, setSearchTermAccounts] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalContent, setUpgradeModalContent] = useState({});
  
  // Load available options when expanded
  useEffect(() => {
    if (isExpanded && (activeTab === 'leads' || activeTab === 'accounts')) {
      loadAvailableOptions();
    }
  }, [isExpanded, activeTab]);
  
  // Initialize messages when messages tab is opened
  useEffect(() => {
    if (activeTab === 'messages' && !messages) {
      initializeMessages();
    }
  }, [activeTab]);
  
  const initializeMessages = () => {
    // Convert campaign messages to the format expected by MessageSequencesFull
    const sequences = {};
    
    if (campaign.campaign_messages && campaign.campaign_messages.length > 0) {
      // First pass: create sequences based on openers
      campaign.campaign_messages.forEach(msg => {
        if (msg.message_type?.startsWith('opener')) {
          // Extract sequence ID (a, b, etc.) from opener_a, opener_b
          const sequenceId = msg.message_type.replace('opener_', '') || 'a';
          sequences[sequenceId] = {
            id: `sequence_${Object.keys(sequences).length}`,
            name: `Sequence ${sequenceId.toUpperCase()}`,
            enabled: msg.is_active !== false,
            opener: {
              id: `opener_${Object.keys(sequences).length}`,
              text: msg.message_text || '',
              enabled: msg.is_active !== false
            },
            followups: []
          };
        }
      });
      
      // If no openers found but we have messages, create default sequence
      if (Object.keys(sequences).length === 0) {
        sequences['a'] = {
          id: 'sequence_0',
          name: 'Sequence A',
          enabled: true,
          opener: {
            id: 'opener_0',
            text: '',
            enabled: true
          },
          followups: []
        };
      }
      
      // Second pass: add follow-ups to their sequences
      campaign.campaign_messages.forEach(msg => {
        if (msg.message_type?.includes('followup')) {
          // Parse followup_1_a or followup_1 or followup1
          let followupLevel;
          let sequenceId;
          
          // Try format: followup_1_a
          let match = msg.message_type.match(/followup_(\d+)_([a-z])/i);
          if (match) {
            followupLevel = parseInt(match[1]);
            sequenceId = match[2];
          } else {
            // Try format: followup1 or followup_1
            match = msg.message_type.match(/followup_?(\d+)$/i);
            if (match) {
              followupLevel = parseInt(match[1]);
              // For generic followups, add to first sequence
              sequenceId = Object.keys(sequences)[0] || 'a';
            }
          }
          
          if (followupLevel && sequenceId && sequences[sequenceId]) {
            sequences[sequenceId].followups.push({
              id: `followup_${sequenceId}_${followupLevel - 1}`,
              text: msg.message_text || '',
              enabled: msg.is_active !== false,
              delay_days: msg.delay_days || followupLevel * 3,
              level: followupLevel
            });
          }
        }
      });
      
      // Sort follow-ups by level
      Object.values(sequences).forEach(seq => {
        seq.followups.sort((a, b) => a.level - b.level);
      });
    } else {
      // No messages found, create default empty sequence
      sequences['a'] = {
        id: 'sequence_0',
        name: 'Sequence A',
        enabled: true,
        opener: {
          id: 'opener_0',
          text: '',
          enabled: true
        },
        followups: []
      };
    }
    
    // Convert to array
    const messageSequences = Object.values(sequences);
    
    setMessages({ 
      messageSequences,
      messageSettings: {
        useAIOptimization: false
      }
    });
  };
  
  const loadAvailableOptions = async () => {
    try {
      setLoadingOptions(true);
      const [lists, accounts] = await Promise.all([
        leadsListService.getLeadsLists(),
        instagramAccountService.getAccounts()
      ]);
      setAvailableLeadLists(lists);
      setAvailableAccounts(accounts);
    } catch (error) {
      console.error('Failed to load options:', error);
    } finally {
      setLoadingOptions(false);
    }
  };
  
  // Track changes
  const checkForChanges = () => {
    const campaignChanged = 
      editedCampaign.name !== campaign.name ||
      editedCampaign.description !== (campaign.description || '') ||
      editedCampaign.daily_message_min !== campaign.daily_message_min ||
      editedCampaign.daily_message_max !== campaign.daily_message_max ||
      editedCampaign.send_time_start !== campaign.send_time_start?.slice(0, 5) ||
      editedCampaign.send_time_end !== campaign.send_time_end?.slice(0, 5) ||
      editedCampaign.timezone !== campaign.timezone ||
      editedCampaign.exclude_weekends !== campaign.exclude_weekends ||
      editedCampaign.exclude_holidays !== campaign.exclude_holidays;
    
    const leadsChanged = 
      JSON.stringify(selectedLeadLists.map(l => l.id).sort()) !== 
      JSON.stringify((campaign.campaign_leads_lists || []).map(cll => cll.leads_lists.id).sort());
    
    const accountsChanged = 
      JSON.stringify(selectedAccounts.map(a => ({ id: a.id, active: a.is_active })).sort((a, b) => a.id.localeCompare(b.id))) !==
      JSON.stringify((campaign.campaign_accounts || []).map(ca => ({ id: ca.instagram_accounts.id, active: ca.is_active })).sort((a, b) => a.id.localeCompare(b.id)));
    
    setHasChanges(campaignChanged || leadsChanged || accountsChanged || messages !== null);
  };
  
  useEffect(() => {
    checkForChanges();
  }, [editedCampaign, selectedLeadLists, selectedAccounts, messages]);
  
  const handleAddAccount = async (account) => {
    try {
      const canAddResult = await subscriptionService.canAddAccountToCampaign();
      
      if (!canAddResult.allowed) {
        // Show upgrade modal
        setUpgradeModalContent({
          title: 'Account Limit Reached',
          message: canAddResult.message,
          tier: canAddResult.tier,
          currentUsage: canAddResult.currentUsage,
          limit: canAddResult.limit,
          upgradeRequired: canAddResult.upgradeRequired,
          canIncreaseQuantity: canAddResult.canIncreaseQuantity
        });
        setShowUpgradeModal(true);
        return;
      }
      
      // Allow the account to be added
      setSelectedAccounts([...selectedAccounts, { ...account, is_active: true }]);
      
    } catch (error) {
      console.error('Error checking subscription limits:', error);
      // If there's an error, still allow for now but log it
      setSelectedAccounts([...selectedAccounts, { ...account, is_active: true }]);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveError(null);
      
      // Check if time window changed
      const timeWindowChanged = 
        editedCampaign.send_time_start !== campaign.send_time_start?.substring(0, 5) ||
        editedCampaign.send_time_end !== campaign.send_time_end?.substring(0, 5);
      
      // Update campaign basic info
      await campaignService.updateCampaign(campaign.id, {
        ...editedCampaign,
        send_time_start: editedCampaign.send_time_start + ':00',
        send_time_end: editedCampaign.send_time_end + ':00'
      });
      
      // If time window changed, invalidate reserved tasks
      if (timeWindowChanged) {
        await campaignService.invalidateReservedTasks(campaign.id);
      }
      
      // Update lead lists if changed
      const currentLeadIds = (campaign.campaign_leads_lists || []).map(cll => cll.leads_lists.id);
      const newLeadIds = selectedLeadLists.map(l => l.id);
      
      const leadsToRemove = currentLeadIds.filter(id => !newLeadIds.includes(id));
      const leadsToAdd = newLeadIds.filter(id => !currentLeadIds.includes(id));
      
      if (leadsToRemove.length > 0) {
        await campaignLeadsListService.removeLeadsListsFromCampaign(campaign.id, leadsToRemove);
      }
      if (leadsToAdd.length > 0) {
        await campaignLeadsListService.addLeadsListsToCampaign(campaign.id, leadsToAdd);
      }
      
      // Update accounts if changed
      const currentAccountIds = (campaign.campaign_accounts || []).map(ca => ca.instagram_accounts.id);
      const newAccountIds = selectedAccounts.map(a => a.id);
      
      const accountsToRemove = currentAccountIds.filter(id => !newAccountIds.includes(id));
      const accountsToAdd = newAccountIds.filter(id => !currentAccountIds.includes(id));
      
      for (const accountId of accountsToRemove) {
        await campaignAccountService.removeAccountFromCampaign(campaign.id, accountId);
      }
      for (const accountId of accountsToAdd) {
        await campaignAccountService.addAccountToCampaign(campaign.id, accountId);
      }
      
      // Update messages if changed
      if (messages && messages.messageSequences) {
        // Delete existing messages first
        const existingMessages = campaign.campaign_messages || [];
        for (const msg of existingMessages) {
          await campaignMessageService.deleteCampaignMessage(msg.id);
        }
        
        // Create new messages from sequences
        const messagesToSave = [];
        
        // Process each sequence
        messages.messageSequences?.forEach((sequence, seqIndex) => {
          if (sequence.enabled) {
            // Add opener for this sequence
            if (sequence.opener && sequence.opener.text) {
              const sequenceLetter = String.fromCharCode(97 + seqIndex); // a, b, c, etc.
              messagesToSave.push({
                campaign_id: campaign.id,
                message_type: `opener_${sequenceLetter}`,
                message_text: sequence.opener.text,
                is_active: sequence.opener.enabled !== false,
                delay_days: 0
              });
              
              // Add follow-ups for this sequence
              sequence.followups?.forEach((followup, followupIndex) => {
                if (followup.enabled && followup.text) {
                  // For first sequence, use simple format (followup1, followup2)
                  // For other sequences, use sequence-specific format (followup_1_b, followup_2_b)
                  const messageType = seqIndex === 0 
                    ? `followup${followupIndex + 1}`
                    : `followup_${followupIndex + 1}_${sequenceLetter}`;
                  
                  messagesToSave.push({
                    campaign_id: campaign.id,
                    message_type: messageType,
                    message_text: followup.text,
                    is_active: followup.enabled !== false,
                    delay_days: followup.delay_days || (followupIndex + 1) * 3
                  });
                }
              });
            }
          }
        });
        
        // Save all messages
        for (const msg of messagesToSave) {
          await campaignMessageService.addCampaignMessage(msg);
        }
      }
      
      setHasChanges(false);
      if (onUpdate) onUpdate();
      
    } catch (error) {
      console.error('Failed to save campaign:', error);
      setSaveError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleCancel = () => {
    // Reset all changes
    setEditedCampaign({
      name: campaign.name,
      description: campaign.description || '',
      daily_message_min: campaign.daily_message_min || 20,
      daily_message_max: campaign.daily_message_max || 50,
      send_time_start: campaign.send_time_start?.slice(0, 5) || '09:00',
      send_time_end: campaign.send_time_end?.slice(0, 5) || '17:00',
      timezone: campaign.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      exclude_weekends: campaign.exclude_weekends || false,
      exclude_holidays: campaign.exclude_holidays || false
    });
    setSelectedLeadLists(campaign.campaign_leads_lists?.map(cll => cll.leads_lists) || []);
    setSelectedAccounts(campaign.campaign_accounts?.map(ca => ca.instagram_accounts) || []);
    setMessages(null);
    setHasChanges(false);
    setSaveError(null);
  };
  
  // Calculate stats
  const totalLeads = campaign.campaign_leads_lists?.reduce((total, cll) => 
    total + (cll.leads_lists?.total_leads || 0), 0) || 0;
  
  // Total messages includes openers + follow-ups
  const openersSent = campaign.messages_sent || 0;
  const followupsSent = campaign.followups_sent || 0;
  const totalMessagesSent = openersSent + followupsSent;
  
  // Leads contacted is just the number of initial messages (openers) sent
  const leadsContacted = openersSent;
  
  const messagestoday = campaign.messages_sent_today || 0;
  const responseRate = campaign.response_rate || 0;
  const progress = totalLeads > 0 ? Math.min((leadsContacted / totalLeads) * 100, 100) : 0;
  
  // Get status color
  const getStatusColor = () => {
    switch (campaign.status) {
      case 'active': return darkMode ? 'text-emerald-400' : 'text-emerald-600';
      case 'paused': return darkMode ? 'text-amber-400' : 'text-amber-600';
      case 'completed': return darkMode ? 'text-accent' : 'text-accent';
      case 'draft': return isReady 
        ? (darkMode ? 'text-green-400' : 'text-green-600')
        : (darkMode ? 'text-secondary' : 'text-gray-500');
      default: return darkMode ? 'text-secondary' : 'text-gray-500';
    }
  };
  
  const getStatusText = () => {
    if (campaign.status === 'draft' && isReady) return 'Ready';
    if (campaign.status === 'draft') return 'Setup';
    return campaign.status;
  };
  
  const tabs = [
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'accounts', label: 'Accounts', icon: Users },
    { id: 'replies', label: 'Replies', icon: Inbox }
  ];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${
        darkMode 
          ? 'bg-surface border-border hover:bg-surface/80' 
          : 'bg-white border-gray-200 hover:bg-gray-50/50'
      } border rounded transition-all duration-200 mb-3 overflow-hidden`}
    >
      {/* Main Row */}
      <div 
        className={`px-4 py-3 lg:px-6 lg:py-4 cursor-pointer ${
          isExpanded ? (darkMode ? 'bg-surface/50' : 'bg-gray-50/50') : ''
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          {/* Left Section - Campaign Info */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 flex-1 min-w-0">
            {/* Expand/Collapse Icon */}
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className={`w-5 h-5 ${darkMode ? 'text-secondary' : 'text-gray-400'}`} />
            </motion.div>
            
            {/* Campaign Name & Status */}
            <div className="min-w-[200px]">
              <div className="flex items-center space-x-2 mb-1">
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {campaign.name}
                </h3>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${getStatusColor()} ${
                  darkMode ? 'bg-elevated/50' : 'bg-gray-100'
                }`}>
                  {getStatusText()}
                </span>
              </div>
              <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                Created {format(new Date(campaign.created_at), 'MMM d, yyyy')}
              </p>
            </div>
            
            {/* Stats Grid */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 flex-1">
              {/* Reply Rate */}
              <div className="flex flex-col">
                <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-500'} mb-1`}>
                  Reply Rate
                </span>
                <div className="flex items-center space-x-1">
                  <TrendingUp className={`w-4 h-4 ${responseRate > 0 ? 'text-emerald-500' : darkMode ? 'text-slate-600' : 'text-gray-400'}`} />
                  <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {responseRate.toFixed(1)}%
                  </span>
                </div>
              </div>
              
              {/* Leads Contacted */}
              <div className="flex flex-col">
                <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-500'} mb-1`}>
                  Leads Contacted
                </span>
                <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {leadsContacted.toLocaleString()}
                </span>
              </div>
              
              {/* Total Messages */}
              <div className="flex flex-col">
                <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-500'} mb-1`}>
                  Total Messages
                </span>
                <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {totalMessagesSent.toLocaleString()}
                  {followupsSent > 0 && (
                    <span className={`text-xs ml-1 ${darkMode ? 'text-secondary' : 'text-gray-500'}`}>
                      ({followupsSent} follow-ups)
                    </span>
                  )}
                </span>
              </div>
              
              {/* Messages Today */}
              <div className="flex flex-col">
                <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-500'} mb-1`}>
                  Today
                </span>
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4 text-accent" />
                  <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {messagestoday}
                  </span>
                </div>
              </div>
              
              {/* Progress */}
              <div className="w-full sm:w-auto sm:flex-1 sm:max-w-[200px]">
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                    Progress
                  </span>
                  <span className={`text-xs font-medium ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                    {progress.toFixed(0)}%
                  </span>
                </div>
                <div className={`w-full h-2 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded-full overflow-hidden`}>
                  <motion.div 
                    className={`h-full rounded-full ${
                      darkMode ? 'bg-gradient-to-r from-accent to-accent-hover' : 'bg-gradient-to-r from-accent to-accent-hover'
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Section - Actions */}
          <div className="flex items-center space-x-2 lg:ml-6" onClick={(e) => e.stopPropagation()}>
            {campaign.status === 'active' ? (
              <button 
                onClick={() => onPause(campaign.id)}
                className={`p-2 rounded transition-all duration-200 ${
                  darkMode 
                    ? 'text-amber-400 hover:bg-amber-500/20' 
                    : 'text-yellow-600 hover:bg-yellow-50'
                }`}
                title="Pause Campaign"
              >
                <Pause className="w-4 h-4" />
              </button>
            ) : (campaign.status === 'paused' || (campaign.status === 'draft' && isReady)) ? (
              <button 
                onClick={() => onStart(campaign.id)}
                disabled={isStarting}
                className={`p-2 rounded transition-all duration-200 ${
                  isStarting 
                    ? (darkMode ? 'text-emerald-600 bg-emerald-500/30' : 'text-green-700 bg-green-100')
                    : (darkMode 
                      ? 'text-emerald-400 hover:bg-emerald-500/20' 
                      : 'text-green-600 hover:bg-green-50')
                }`}
                title={isStarting ? "Starting..." : "Start Campaign"}
              >
                {isStarting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
              </button>
            ) : null}
            
            <button 
              onClick={() => onDelete(campaign)}
              className={`p-2 rounded transition-all duration-200 ${
                darkMode 
                  ? 'text-red-400 hover:bg-red-500/20' 
                  : 'text-red-600 hover:bg-red-50'
              }`}
              title="Delete Campaign"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Expandable Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`border-t ${darkMode ? 'border-border' : 'border-gray-200'}`}
          >
            {/* Tab Navigation */}
            <div className={`flex items-center justify-between p-2 ${darkMode ? 'bg-surface/30' : 'bg-gray-50'}`}>
              <div className="flex space-x-1">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveTab(tab.id);
                      }}
                      className={`flex items-center space-x-2 px-4 py-2 rounded text-sm font-medium transition-colors ${
                        activeTab === tab.id
                          ? darkMode 
                            ? 'bg-elevated text-accent' 
                            : 'bg-white text-accent shadow-sm'
                          : darkMode
                            ? 'text-secondary hover:text-primary hover:bg-elevated/50'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
              
              {/* Save/Cancel buttons */}
              {hasChanges && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                      darkMode 
                        ? 'text-secondary hover:bg-elevated hover:text-primary' 
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className={`px-4 py-1.5 text-sm font-medium rounded transition-colors flex items-center space-x-2 ${
                      isSaving
                        ? darkMode ? 'bg-elevated text-muted' : 'bg-gray-300 text-gray-500'
                        : darkMode 
                          ? 'bg-accent text-primary hover:bg-accent-hover' 
                          : 'bg-accent text-primary hover:bg-accent-hover'
                    }`}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
            
            {/* Error message */}
            {saveError && (
              <div className={`mx-6 mt-2 p-3 rounded flex items-center space-x-2 ${
                darkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-50 text-red-600'
              }`}>
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{saveError}</span>
              </div>
            )}
            
            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'settings' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
                        Campaign Name
                      </label>
                      <input
                        type="text"
                        value={editedCampaign.name}
                        onChange={(e) => setEditedCampaign({ ...editedCampaign, name: e.target.value })}
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent ${
                          darkMode 
                            ? 'bg-surface border-border text-primary focus:ring-accent' 
                            : 'bg-white border-gray-300 text-gray-900 focus:ring-accent'
                        }`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
                        Timezone
                      </label>
                      <select
                        value={editedCampaign.timezone}
                        onChange={(e) => setEditedCampaign({ ...editedCampaign, timezone: e.target.value })}
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent ${
                          darkMode 
                            ? 'bg-surface border-border text-primary focus:ring-accent' 
                            : 'bg-white border-gray-300 text-gray-900 focus:ring-accent'
                        }`}
                      >
                        <optgroup label="US Timezones">
                          <option value="America/New_York">Eastern Time (ET)</option>
                          <option value="America/Chicago">Central Time (CT)</option>
                          <option value="America/Denver">Mountain Time (MT)</option>
                          <option value="America/Phoenix">Mountain Time - Arizona (MST)</option>
                          <option value="America/Los_Angeles">Pacific Time (PT)</option>
                          <option value="America/Anchorage">Alaska Time (AKT)</option>
                          <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
                        </optgroup>
                        <optgroup label="Europe">
                          <option value="Europe/London">London (GMT/BST)</option>
                          <option value="Europe/Paris">Paris (CET)</option>
                          <option value="Europe/Berlin">Berlin (CET)</option>
                          <option value="Europe/Madrid">Madrid (CET)</option>
                          <option value="Europe/Rome">Rome (CET)</option>
                          <option value="Europe/Amsterdam">Amsterdam (CET)</option>
                          <option value="Europe/Stockholm">Stockholm (CET)</option>
                          <option value="Europe/Moscow">Moscow (MSK)</option>
                        </optgroup>
                        <optgroup label="Asia Pacific">
                          <option value="Asia/Dubai">Dubai (GST)</option>
                          <option value="Asia/Mumbai">Mumbai (IST)</option>
                          <option value="Asia/Bangkok">Bangkok (ICT)</option>
                          <option value="Asia/Singapore">Singapore (SGT)</option>
                          <option value="Asia/Hong_Kong">Hong Kong (HKT)</option>
                          <option value="Asia/Shanghai">Shanghai (CST)</option>
                          <option value="Asia/Tokyo">Tokyo (JST)</option>
                          <option value="Asia/Seoul">Seoul (KST)</option>
                          <option value="Australia/Sydney">Sydney (AEDT)</option>
                          <option value="Australia/Melbourne">Melbourne (AEDT)</option>
                          <option value="Pacific/Auckland">Auckland (NZDT)</option>
                        </optgroup>
                        <optgroup label="Americas">
                          <option value="America/Toronto">Toronto (ET)</option>
                          <option value="America/Vancouver">Vancouver (PT)</option>
                          <option value="America/Mexico_City">Mexico City (CST)</option>
                          <option value="America/Sao_Paulo">São Paulo (BRT)</option>
                          <option value="America/Buenos_Aires">Buenos Aires (ART)</option>
                          <option value="America/Bogota">Bogotá (COT)</option>
                        </optgroup>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
                      Description
                    </label>
                    <textarea
                      value={editedCampaign.description}
                      onChange={(e) => setEditedCampaign({ ...editedCampaign, description: e.target.value })}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent ${
                        darkMode 
                          ? 'bg-surface border-border text-primary focus:ring-accent' 
                          : 'bg-white border-gray-300 text-gray-900 focus:ring-accent'
                      }`}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
                        Min Daily Messages
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={editedCampaign.daily_message_min}
                        onChange={(e) => setEditedCampaign({ ...editedCampaign, daily_message_min: parseInt(e.target.value) })}
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent ${
                          darkMode 
                            ? 'bg-surface border-border text-primary focus:ring-accent' 
                            : 'bg-white border-gray-300 text-gray-900 focus:ring-accent'
                        }`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
                        Max Daily Messages
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={editedCampaign.daily_message_max}
                        onChange={(e) => setEditedCampaign({ ...editedCampaign, daily_message_max: parseInt(e.target.value) })}
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent ${
                          darkMode 
                            ? 'bg-surface border-border text-primary focus:ring-accent' 
                            : 'bg-white border-gray-300 text-gray-900 focus:ring-accent'
                        }`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={editedCampaign.send_time_start}
                        onChange={(e) => setEditedCampaign({ ...editedCampaign, send_time_start: e.target.value })}
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent ${
                          darkMode 
                            ? 'bg-surface border-border text-primary focus:ring-accent' 
                            : 'bg-white border-gray-300 text-gray-900 focus:ring-accent'
                        }`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
                        End Time
                      </label>
                      <input
                        type="time"
                        value={editedCampaign.send_time_end}
                        onChange={(e) => setEditedCampaign({ ...editedCampaign, send_time_end: e.target.value })}
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent ${
                          darkMode 
                            ? 'bg-surface border-border text-primary focus:ring-accent' 
                            : 'bg-white border-gray-300 text-gray-900 focus:ring-accent'
                        }`}
                      />
                    </div>
                  </div>
                  
                  <div className="flex space-x-6">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editedCampaign.exclude_weekends}
                        onChange={(e) => setEditedCampaign({ ...editedCampaign, exclude_weekends: e.target.checked })}
                        className="rounded"
                      />
                      <span className={`text-sm ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
                        Exclude weekends
                      </span>
                    </label>
                    
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editedCampaign.exclude_holidays}
                        onChange={(e) => setEditedCampaign({ ...editedCampaign, exclude_holidays: e.target.checked })}
                        className="rounded"
                      />
                      <span className={`text-sm ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
                        Exclude holidays
                      </span>
                    </label>
                  </div>
                </div>
              )}
              
              {activeTab === 'leads' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className={`text-sm font-medium ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
                        Selected Lead Lists
                      </p>
                      <p className={`text-xs ${darkMode ? 'text-muted' : 'text-gray-500'} mt-1`}>
                        Total leads: {selectedLeadLists.reduce((total, list) => total + (list.total_leads || 0), 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="relative">
                      <Search className={`w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 ${
                        darkMode ? 'text-secondary' : 'text-gray-400'
                      }`} />
                      <input
                        type="text"
                        placeholder="Search lead lists..."
                        value={searchTermLeads}
                        onChange={(e) => setSearchTermLeads(e.target.value)}
                        className={`pl-9 pr-3 py-1.5 text-sm border rounded w-48 ${
                          darkMode 
                            ? 'bg-surface border-border text-primary placeholder-secondary' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                        }`}
                      />
                    </div>
                  </div>
                  
                  {/* Selected lead lists */}
                  <div className="space-y-2 mb-4">
                    {selectedLeadLists.length === 0 ? (
                      <div className={`text-center py-4 rounded border ${
                        darkMode ? 'border-border text-muted' : 'border-gray-300 text-gray-500'
                      }`}>
                        <p className="text-sm">No lead lists selected</p>
                      </div>
                    ) : (
                      selectedLeadLists.map(list => (
                        <div 
                          key={list.id}
                          className={`p-3 rounded flex justify-between items-center ${
                            darkMode ? 'bg-elevated/50' : 'bg-gray-50'
                          }`}
                        >
                          <div>
                            <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {list.name}
                            </span>
                            <span className={`text-sm ml-2 ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                              ({list.total_leads || 0} leads)
                            </span>
                          </div>
                          <button
                            onClick={() => setSelectedLeadLists(selectedLeadLists.filter(l => l.id !== list.id))}
                            className={`p-1 rounded hover:bg-red-500/20 transition-colors ${
                              darkMode ? 'text-red-400' : 'text-red-600'
                            }`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                  
                  {/* Available lead lists */}
                  <div>
                    <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                      Available Lead Lists
                    </p>
                    {loadingOptions ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin" />
                      </div>
                    ) : availableLeadLists.filter(list => 
                        !selectedLeadLists.find(s => s.id === list.id) &&
                        list.name.toLowerCase().includes(searchTermLeads.toLowerCase())
                      ).length === 0 ? (
                      <div className={`text-center py-8 rounded border ${
                        darkMode ? 'border-border bg-surface/50' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <Users className="w-8 h-8 mx-auto mb-3 opacity-50" />
                        <p className={`text-sm mb-3 ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                          {searchTermLeads ? 'No matching lead lists found' : 'No available lead lists'}
                        </p>
                        {!searchTermLeads && (
                          <button
                            onClick={() => navigate('/dashboard/leads')}
                            className={`inline-flex items-center space-x-2 px-4 py-2 rounded text-sm font-medium transition-colors ${
                              darkMode 
                                ? 'bg-accent/20 text-accent hover:bg-accent/30' 
                                : 'bg-accent/10 text-accent hover:bg-accent/20'
                            }`}
                          >
                            <Plus className="w-4 h-4" />
                            <span>Create Lead Lists</span>
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {availableLeadLists
                          .filter(list => 
                            !selectedLeadLists.find(s => s.id === list.id) &&
                            list.name.toLowerCase().includes(searchTermLeads.toLowerCase())
                          )
                          .map(list => (
                            <button
                              key={list.id}
                              onClick={() => setSelectedLeadLists([...selectedLeadLists, list])}
                              className={`w-full p-3 rounded flex justify-between items-center transition-colors ${
                                darkMode 
                                  ? 'bg-surface hover:bg-elevated text-left' 
                                  : 'bg-white hover:bg-gray-50 text-left border border-gray-200'
                              }`}
                            >
                              <div>
                                <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {list.name}
                                </span>
                                <span className={`text-sm ml-2 ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                                  ({list.total_leads || 0} leads)
                                </span>
                              </div>
                              <Plus className={`w-4 h-4 ${darkMode ? 'text-accent' : 'text-accent'}`} />
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {activeTab === 'messages' && (
                <div>
                  {messages ? (
                    <MessageSequencesFull 
                      data={{ messageSequences: messages.messageSequences, messageSettings }}
                      onDataChange={(data) => {
                        setMessages({ messageSequences: data.messageSequences });
                        setMessageSettings(data.messageSettings);
                      }}
                      darkMode={darkMode}
                    />
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'accounts' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <p className={`text-sm font-medium ${darkMode ? 'text-primary' : 'text-gray-700'}`}>
                      Instagram Accounts
                    </p>
                    <div className="relative">
                      <Search className={`w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 ${
                        darkMode ? 'text-secondary' : 'text-gray-400'
                      }`} />
                      <input
                        type="text"
                        placeholder="Search accounts..."
                        value={searchTermAccounts}
                        onChange={(e) => setSearchTermAccounts(e.target.value)}
                        className={`pl-9 pr-3 py-1.5 text-sm border rounded w-48 ${
                          darkMode 
                            ? 'bg-surface border-border text-primary placeholder-secondary' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                        }`}
                      />
                    </div>
                  </div>
                  
                  {/* Selected accounts */}
                  <div className="space-y-2 mb-4">
                    {selectedAccounts.length === 0 ? (
                      <div className={`text-center py-4 rounded border ${
                        darkMode ? 'border-border text-muted' : 'border-gray-300 text-gray-500'
                      }`}>
                        <p className="text-sm">No accounts selected</p>
                      </div>
                    ) : (
                      selectedAccounts.map(account => (
                        <div 
                          key={account.id}
                          className={`p-3 rounded flex items-center justify-between ${
                            darkMode ? 'bg-elevated/50' : 'bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <img
                              src={`https://ui-avatars.com/api/?name=${account.username}&background=${ACCENT_COLOR_HEX}&color=ffffff&size=32`}
                              alt={account.username}
                              className="w-8 h-8 rounded-full"
                            />
                            <div>
                              <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                @{account.username}
                              </p>
                              <p className={`text-xs ${darkMode ? 'text-muted' : 'text-gray-500'}`}>
                                {account.followers_count || 0} followers
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => setSelectedAccounts(selectedAccounts.filter(a => a.id !== account.id))}
                            className={`p-1.5 rounded hover:bg-red-500/20 transition-colors ${
                              darkMode ? 'text-red-400' : 'text-red-600'
                            }`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                  
                  {/* Available accounts */}
                  <div>
                    <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                      Available Accounts
                    </p>
                    {loadingOptions ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin" />
                      </div>
                    ) : availableAccounts.filter(account => 
                        !selectedAccounts.find(s => s.id === account.id) &&
                        account.username.toLowerCase().includes(searchTermAccounts.toLowerCase())
                      ).length === 0 ? (
                      <div className={`text-center py-8 rounded border ${
                        darkMode ? 'border-border bg-surface/50' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <Users className="w-8 h-8 mx-auto mb-3 opacity-50" />
                        <p className={`text-sm mb-3 ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                          {searchTermAccounts ? 'No matching accounts found' : 'No available Instagram accounts'}
                        </p>
                        {!searchTermAccounts && (
                          <button
                            onClick={() => navigate('/dashboard/accounts')}
                            className={`inline-flex items-center space-x-2 px-4 py-2 rounded text-sm font-medium transition-colors ${
                              darkMode 
                                ? 'bg-accent/20 text-accent hover:bg-accent/30' 
                                : 'bg-accent/10 text-accent hover:bg-accent/20'
                            }`}
                          >
                            <Plus className="w-4 h-4" />
                            <span>Connect Instagram Account</span>
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {availableAccounts
                          .filter(account => 
                            !selectedAccounts.find(s => s.id === account.id) &&
                            account.username.toLowerCase().includes(searchTermAccounts.toLowerCase())
                          )
                          .map(account => (
                            <button
                              key={account.id}
                              onClick={() => handleAddAccount(account)}
                              className={`w-full p-3 rounded flex justify-between items-center transition-colors ${
                                darkMode 
                                  ? 'bg-slate-800 hover:bg-slate-700' 
                                  : 'bg-white hover:bg-gray-50 border border-gray-200'
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <img
                                  src={`https://ui-avatars.com/api/?name=${account.username}&background=${ACCENT_COLOR_HEX}&color=ffffff&size=32`}
                                  alt={account.username}
                                  className="w-8 h-8 rounded-full"
                                />
                                <div className="text-left">
                                  <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    @{account.username}
                                  </p>
                                  <p className={`text-xs ${darkMode ? 'text-muted' : 'text-gray-500'}`}>
                                    {account.followers_count || 0} followers
                                  </p>
                                </div>
                              </div>
                              <Plus className={`w-4 h-4 ${darkMode ? 'text-accent' : 'text-accent'}`} />
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {activeTab === 'replies' && (
                <div className={`text-center py-8 ${darkMode ? 'text-secondary' : 'text-gray-500'}`}>
                  <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Replies and conversations will be displayed here</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upgrade Modal */}
      <AnimatePresence>
        {showUpgradeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowUpgradeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`max-w-md w-full rounded p-6 ${
                darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'
              } border shadow-xl`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {upgradeModalContent.title}
                </h3>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className={`p-1 rounded hover:bg-opacity-20 hover:bg-gray-500 ${
                    darkMode ? 'text-secondary' : 'text-gray-500'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className={`mb-4 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                {upgradeModalContent.message}
              </p>
              
              <div className={`p-3 rounded mb-4 ${darkMode ? 'bg-elevated' : 'bg-gray-50'}`}>
                <div className={`flex justify-between text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <span>Current usage:</span>
                  <span className="font-medium">{upgradeModalContent.currentUsage}</span>
                </div>
                <div className={`flex justify-between text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <span>Limit:</span>
                  <span className="font-medium">{upgradeModalContent.limit}</span>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className={`flex-1 py-2 px-4 rounded border transition-colors ${
                    darkMode 
                      ? 'border-border text-primary hover:bg-elevated' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowUpgradeModal(false);
                    navigate('/pricing');
                  }}
                  className="flex-1 py-2 px-4 rounded bg-accent hover:bg-accent-hover text-primary transition-colors"
                >
                  Upgrade Plan
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CampaignRow;